import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled Components
const MaintenanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1.5rem;
  background: linear-gradient(-45deg, #121212, #1a1a2e, #16213e, #0f3460);
  background-size: 400% 400%;
  animation: ${() => css`${gradient} 15s ease infinite`};
  position: relative;
  overflow: hidden;
`;

const SpaceIllustration = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 0.3;
`;

const Circle = styled.div<{ duration?: string; delay?: string }>`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: ${(props) => css`
    ${float} ${props.duration || '5s'} ease-in-out infinite ${props.delay || '0s'}
  `};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  z-index: 1;
  max-width: 100%;
  width: 100%;
  gap: 1rem;
  padding: 1rem;
`;

const MaintenanceTitle = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 800;
  margin: 0;
  background: linear-gradient(90deg, #00dbde, #fc00ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    font-size: 6rem;
    flex-direction: row;
  }
`;

const MaintenanceDescription = styled(motion.p)`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.4;
  max-width: 90%;

  @media (min-width: 768px) {
    font-size: 1.5rem;
    max-width: 600px;
  }
`;

const MaintenanceSubtext = styled(motion.p)`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.4;
  max-width: 90%;

  @media (min-width: 768px) {
    font-size: 1.1rem;
    max-width: 600px;
  }
`;

const RefreshButton = styled(motion.button)`
  background: linear-gradient(45deg, #ccff00, #00b4db);
  color: #111;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  margin-top: 1.5rem;
  position: relative;
  overflow: hidden;
  z-index: 1;
  min-width: 200px;
  min-height: 50px;
  touch-action: manipulation;

  &:hover, &:focus {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ToolIcon = styled.div`
  font-size: 2.5rem;
  animation: ${() => css`${float} 3s ease-in-out infinite`};
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const createStars = () => {
  const stars = [];
  const starCount = window.innerWidth < 768 ? 30 : 50;
  
  for (let i = 0; i < starCount; i++) {
    const size = Math.random() * (window.innerWidth < 768 ? 2 : 3) + 1;
    stars.push(
      <Circle
        key={i}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
        }}
        duration={`${Math.random() * 10 + 5}s`}
        delay={`${Math.random() * 5}s`}
      />
    );
  }
  return stars;
};

const MaintenancePage = () => {
  const navigate = useNavigate();

  return (
    <MaintenanceContainer>
      <SpaceIllustration>{createStars()}</SpaceIllustration>

      <ContentWrapper>
        <MaintenanceTitle
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 100 }}
        >
          <ToolIcon>🔧</ToolIcon>
          Maintenance
        </MaintenanceTitle>

        <MaintenanceDescription
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          We're working on some improvements
        </MaintenanceDescription>

        <MaintenanceSubtext
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          Our team is performing scheduled maintenance. We'll be back soon!
        </MaintenanceSubtext>

        <RefreshButton
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate(0)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          Refresh Page
        </RefreshButton>
      </ContentWrapper>
    </MaintenanceContainer>
  );
};

export default MaintenancePage;